'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface UserProfile {
  displayName: string;
  username: string;
  bio: string;
  avatarUrl: string;
  bannerUrl: string;
  website: string;
  role: 'owner' | 'creator' | 'editor' | 'moderator' | 'viewer';
  isProfileComplete: boolean;
  socialLinks?: {
    twitter?: string;
    instagram?: string;
    youtube?: string;
    tiktok?: string;
    twitch?: string;
  };
  isBanned?: boolean;
  assignedTag?: string;
  followerCount?: number;
  followingCount?: number;
  clipsCount?: number;
}

export interface AuthUser {
  id: string;
  email: string;
  profile: UserProfile | null;
}

const OWNER_USERNAME = 'bytez_filmss';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isOwner: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (email: string, password: string, role: 'creator' | 'viewer') => Promise<{ success: boolean; error?: string }>;
  signInWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  updateProfile: (profile: Partial<UserProfile>) => Promise<void>;
  completeProfileSetup: (profile: Omit<UserProfile, 'isProfileComplete'>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function mapDbProfileToUserProfile(dbProfile: any): UserProfile {
  return {
    displayName: dbProfile.display_name || '',
    username: dbProfile.username || '',
    bio: dbProfile.bio || '',
    avatarUrl: dbProfile.avatar_url || '',
    bannerUrl: dbProfile.banner_url || '',
    website: dbProfile.website || '',
    role: (dbProfile.role as UserProfile['role']) || 'viewer',
    isProfileComplete: dbProfile.is_profile_complete || false,
    socialLinks: dbProfile.social_links || {},
    isBanned: dbProfile.is_banned || false,
    assignedTag: dbProfile.assigned_tag || '',
    followerCount: dbProfile.follower_count || 0,
    followingCount: dbProfile.following_count || 0,
    clipsCount: dbProfile.clips_count || 0,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Use the singleton client — never recreate it
  const supabase = createClient();

  const isOwner = user?.profile?.username?.toLowerCase() === OWNER_USERNAME.toLowerCase();

  const fetchProfile = useCallback(async (userId: string, email: string): Promise<AuthUser> => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      return {
        id: userId,
        email,
        profile: profile ? mapDbProfileToUserProfile(profile) : null,
      };
    } catch {
      return { id: userId, email, profile: null };
    }
  }, [supabase]);

  const refreshProfile = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const updated = await fetchProfile(session.user.id, session.user.email || '');
    setUser(updated);
  }, [supabase, fetchProfile]);

  useEffect(() => {
    let mounted = true;

    // Listen for auth state changes — this fires immediately with the current session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (session?.user) {
        const authUser = await fetchProfile(session.user.id, session.user.email || '');
        if (mounted) {
          setUser(authUser);
          setIsLoading(false);
        }
      } else {
        if (mounted) {
          setUser(null);
          setIsLoading(false);
        }
      }
    });

    // Also do an explicit getSession in case onAuthStateChange doesn't fire for existing sessions
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      // Only update if we're still loading (onAuthStateChange hasn't resolved yet)
      if (session?.user) {
        const authUser = await fetchProfile(session.user.id, session.user.email || '');
        if (mounted) {
          setUser(authUser);
          setIsLoading(false);
        }
      } else {
        if (mounted) {
          setUser(null);
          setIsLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: error.message };
    return { success: true };
  }, [supabase]);

  const signInWithGoogle = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  }, [supabase]);

  const signup = useCallback(async (
    email: string,
    password: string,
    role: 'creator' | 'viewer'
  ): Promise<{ success: boolean; error?: string }> => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { role },
      },
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  }, [supabase]);

  const logout = useCallback(async () => {
    setUser(null);
    await supabase.auth.signOut();
  }, [supabase]);

  const updateProfile = useCallback(async (updates: Partial<UserProfile>) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;

    const dbUpdates: Record<string, any> = {};
    if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName;
    if (updates.username !== undefined) dbUpdates.username = updates.username;
    if (updates.bio !== undefined) dbUpdates.bio = updates.bio;
    if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl;
    if (updates.bannerUrl !== undefined) dbUpdates.banner_url = updates.bannerUrl;
    if (updates.website !== undefined) dbUpdates.website = updates.website;
    if (updates.role !== undefined) dbUpdates.role = updates.role;
    if (updates.socialLinks !== undefined) dbUpdates.social_links = updates.socialLinks;
    if (updates.assignedTag !== undefined) dbUpdates.assigned_tag = updates.assignedTag;
    if (updates.isBanned !== undefined) dbUpdates.is_banned = updates.isBanned;

    const { error } = await supabase
      .from('profiles')
      .update(dbUpdates)
      .eq('id', session.user.id);

    if (!error) {
      await refreshProfile();
    }
  }, [supabase, refreshProfile]);

  const completeProfileSetup = useCallback(async (profileData: Omit<UserProfile, 'isProfileComplete'>) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;

    const { error } = await supabase
      .from('profiles')
      .update({
        display_name: profileData.displayName,
        username: profileData.username.toLowerCase(),
        bio: profileData.bio || '',
        avatar_url: profileData.avatarUrl || '',
        banner_url: profileData.bannerUrl || '',
        website: profileData.website || '',
        role: profileData.role,
        social_links: profileData.socialLinks || {},
        is_profile_complete: true,
      })
      .eq('id', session.user.id);

    if (!error) {
      await refreshProfile();
    }
  }, [supabase, refreshProfile]);

  return (
    <AuthContext.Provider value={{ user, isLoading, isOwner, login, signup, signInWithGoogle, logout, updateProfile, completeProfileSetup, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export { OWNER_USERNAME };